import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ImageOff, ShoppingCart } from "lucide-react";
import { getProduct, getProductUnits } from "../services/productService";
import { friendlyError } from "../api/api";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import ErrorState from "../components/ui/ErrorState";
import ListSkeleton from "../components/ui/ListSkeleton";
import { formatMoney } from "../utils/money";

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const { showPrices } = useSettings();
  const [state, setState] = useState({ status: "loading", product: null, units: [], message: "" });
  const [selected, setSelected] = useState(null);
  const [added, setAdded] = useState(false);

  const load = () => {
    setState((s) => ({ ...s, status: "loading" }));
    Promise.all([getProduct(id), getProductUnits(id).catch(() => [])])
      .then(([product, units]) => {
        // Only Active units are selectable (same rule as ProductCard).
        const active = (units || []).filter((u) => u.is_active !== false);
        setSelected(active.find((u) => u.is_base_unit) || active[0] || null);
        setState({ status: "ok", product, units: active, message: "" });
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          setState({ status: "notfound", product: null, units: [], message: "" });
        } else {
          setState({ status: "error", product: null, units: [], message: friendlyError(err, "Unable to load this product.") });
        }
      });
  };

  useEffect(load, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.status === "loading") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <ListSkeleton rows={3} />
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <ErrorState message={state.message} onRetry={load} />
      </div>
    );
  }
  if (state.status === "notfound" || !state.product || state.product.is_active === false) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-sm font-medium text-neutral-700">Product not found.</p>
        <Link to="/shop" className="mt-3 inline-block text-sm text-neutral-600 underline">
          Back to Shop
        </Link>
      </div>
    );
  }

  const p = state.product;
  const price = selected ? selected.selling_price : p.price;
  const unitLabel = selected ? selected.unit_code : p.unit;
  const inStock = p.stock == null || p.stock > 0;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <Link to="/shop" className="text-sm text-neutral-500 underline">
        ← Back to Shop
      </Link>
      <div className="mt-4 grid sm:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-neutral-200 bg-neutral-50 h-64 sm:h-80 flex items-center justify-center overflow-hidden">
          {p.image ? (
            <img src={p.image} alt={p.name} className="max-w-full max-h-full object-contain" />
          ) : (
            <ImageOff className="text-neutral-300" size={36} />
          )}
        </div>
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">{p.name}</h1>
          {p.description && <p className="mt-2 text-sm text-neutral-600">{p.description}</p>}
          <p className="mt-4 text-lg font-bold text-neutral-900">
            {showPrices ? formatMoney(price, p.currency) : "Price on request"}
            {unitLabel && <span className="text-sm font-normal text-neutral-500"> / {unitLabel}</span>}
          </p>
          <p className={`mt-1 text-sm ${inStock ? "text-green-700" : "text-red-600"}`}>
            {inStock ? "In stock" : "Out of stock"}
          </p>

          {state.units.length > 1 && (
            <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Unit">
              {state.units.map((u) => (
                <button
                  key={u.unit_code}
                  type="button"
                  onClick={() => setSelected(u)}
                  className={`text-sm rounded-full border px-3 py-2 ${
                    selected?.unit_code === u.unit_code
                      ? "border-neutral-900 bg-neutral-900 text-white"
                      : "border-neutral-300 text-neutral-600"
                  }`}
                >
                  {u.unit_code}
                </button>
              ))}
            </div>
          )}

          <button
            type="button"
            disabled={!inStock}
            onClick={() => {
              addItem(p, selected, 1);
              setAdded(true);
            }}
            className="mt-6 inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium rounded-lg px-5 py-3 hover:bg-neutral-800 disabled:opacity-50"
          >
            <ShoppingCart size={16} />
            Add to Cart
          </button>
          {added && (
            <p className="mt-3 text-sm text-neutral-600" aria-live="polite">
              Added.{" "}
              <Link to="/cart" className="underline">
                View cart
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
